/**
 * Logique, mode « Ensembles » : deux ou trois ensembles de E = {1, …, 12}
 * définis par une propriété, un diagramme de Venn ; on choisit une expression
 * (complémentaire, ∩, ∪, \) : sa région se colore, ses éléments s'allument, et
 * « x ∈ A ∩ B ⟺ x ∈ A et x ∈ B » fait le lien avec la logique. Inclusion
 * vérifiée élément par élément (contre-exemple), comparaison de deux
 * expressions (lois de Morgan, distributivité).
 */

import { useId, useMemo, useState, type ReactNode } from 'react';
import { MathText, cn } from '@jeraisonne/ui';
import { Note, Segmented, ToggleChip } from '../../ui/controls';
import { fr } from './fr';
import { FIGURE_BG_CLASS, HaloText, INK } from '../../ui/svg-parts';
import {
  SET_DEFS,
  SET_EXPRS_2,
  SET_EXPRS_3,
  SET_NAMES,
  UNIVERSE,
  elementsOf,
  inRegion,
  inclusion,
  listTex,
  memberParts,
  regionOf,
  sameSet,
  setSpeech,
  setTex,
  type SetDef,
  type SetExpr,
} from './logic';
import { VW, circles, rect, slots, vh } from './venn';

const SET_COLORS = [
  'var(--color-primary-500)',
  'var(--color-accent-500)',
  'var(--color-success-500)',
];
const SHADE = 'var(--color-primary-400)';

/** l'expression écrite en HTML (complémentaire = barre au-dessus) */
function ExprLabel({ e }: { e: SetExpr }): ReactNode {
  const atom = (x: SetExpr) => x.k === 'set' || x.k === 'comp';
  const wrap = (x: SetExpr) => (atom(x) ? <ExprLabel e={x} /> : <>({<ExprLabel e={x} />})</>);
  switch (e.k) {
    case 'set':
      return <span className="italic">{SET_NAMES[e.i]}</span>;
    case 'comp':
      return (
        <span className="inline-block border-t-[1.5px] border-current pt-px leading-none">
          <ExprLabel e={e.a} />
        </span>
      );
    case 'inter':
      return (
        <>
          {wrap(e.a)} ∩ {wrap(e.b)}
        </>
      );
    case 'union':
      return (
        <>
          {wrap(e.a)} ∪ {wrap(e.b)}
        </>
      );
    case 'diff':
      return (
        <>
          {wrap(e.a)} \ {wrap(e.b)}
        </>
      );
  }
}

function ExprChips({
  exprs,
  value,
  onChange,
  label,
}: {
  exprs: readonly SetExpr[];
  value: number;
  onChange: (i: number) => void;
  label: string;
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-2">
      {exprs.map((e, i) => (
        <button
          key={i}
          type="button"
          aria-pressed={value === i}
          aria-label={setSpeech(e)}
          onClick={() => onChange(i)}
          className={cn(
            'min-h-11 min-w-11 rounded-xl border px-3 text-base font-semibold',
            value === i
              ? 'border-primary-500 bg-primary-50 text-primary-700'
              : 'border-ink-200 text-ink-700 hover:bg-ink-50',
          )}
        >
          <ExprLabel e={e} />
        </button>
      ))}
    </div>
  );
}

function Venn({
  n,
  sets,
  expr,
  lit,
}: {
  n: 2 | 3;
  sets: readonly SetDef[];
  expr: SetExpr;
  lit: ReadonlySet<number>;
}) {
  const uid = useId().replace(/:/g, '');
  const cs = circles(n);
  const R = rect(n);
  const places = useMemo(() => slots(n), [n]);
  const masks = Array.from({ length: 1 << n }, (_, m) => m).filter((m) => inRegion(expr, m));
  // éléments rangés dans leur région
  const used = new Map<number, number>();
  const pos = UNIVERSE.map((x) => {
    const m = regionOf(x, sets);
    const k = used.get(m) ?? 0;
    used.set(m, k + 1);
    return { x, p: places.get(m)?.[k] ?? { x: R.x + 20, y: R.y + R.h - 12 } };
  });
  return (
    <div className="mx-auto w-full max-w-[26rem]">
      <svg
        viewBox={`0 0 ${VW} ${vh(n)}`}
        className={cn('border-ink-100 block w-full rounded-2xl border', FIGURE_BG_CLASS)}
        role="img"
        aria-label={`Diagramme de Venn : ${setSpeech(expr)} est coloré`}
      >
        <defs>
          {cs.map((c, i) => (
            <clipPath key={i} id={`${uid}-in${i}`}>
              <circle cx={c.x} cy={c.y} r={c.r} />
            </clipPath>
          ))}
          {cs.map((c, i) => (
            <mask key={i} id={`${uid}-out${i}`}>
              <rect x={0} y={0} width={VW} height={vh(n)} fill="white" />
              <circle cx={c.x} cy={c.y} r={c.r} fill="black" />
            </mask>
          ))}
        </defs>
        {/* chaque région de l'expression : dans les cercles de son masque, hors des autres */}
        {masks.map((m) => {
          let node: ReactNode = (
            <rect x={R.x} y={R.y} width={R.w} height={R.h} fill={SHADE} fillOpacity={0.3} />
          );
          cs.forEach((_, i) => {
            node =
              m & (1 << i) ? (
                <g clipPath={`url(#${uid}-in${i})`}>{node}</g>
              ) : (
                <g mask={`url(#${uid}-out${i})`}>{node}</g>
              );
          });
          return (
            <g key={m} data-testid={`venn-region-${m}`}>
              {node}
            </g>
          );
        })}
        <rect
          x={R.x}
          y={R.y}
          width={R.w}
          height={R.h}
          rx={10}
          fill="none"
          stroke="var(--color-ink-400)"
          strokeWidth={1.5}
        />
        <text
          x={R.x + 10}
          y={R.y + 20}
          fontSize={15}
          fontWeight={700}
          fontStyle="italic"
          fill={INK}
        >
          E
        </text>
        {cs.map((c, i) => (
          <g key={i}>
            <circle
              cx={c.x}
              cy={c.y}
              r={c.r}
              fill="none"
              stroke={SET_COLORS[i]}
              strokeWidth={2.5}
            />
            <HaloText
              x={c.lx}
              y={c.ly}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={16}
              fontWeight={700}
              fontStyle="italic"
              fill={SET_COLORS[i]}
            >
              {SET_NAMES[i]}
            </HaloText>
          </g>
        ))}
        {pos.map(({ x, p }) => (
          <text
            key={x}
            x={p.x}
            y={p.y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={14}
            fontWeight={lit.has(x) ? 800 : 500}
            fill={lit.has(x) ? INK : 'var(--color-ink-400)'}
          >
            {x}
          </text>
        ))}
      </svg>
    </div>
  );
}

export function SetsMode() {
  const [n, setN] = useState<2 | 3>(2);
  const [ids, setIds] = useState(['even', 'mul3', 'le6']);
  const [ei, setEi] = useState(3);
  const [cmp, setCmp] = useState(false);
  const [fi, setFi] = useState(10);
  const selectBase = useId();

  const sets = ids.slice(0, n).map((id) => SET_DEFS.find((s) => s.id === id)!);
  const exprs = n === 2 ? SET_EXPRS_2 : [...SET_EXPRS_2, ...SET_EXPRS_3];
  const e = exprs[Math.min(ei, exprs.length - 1)]!;
  const f = exprs[Math.min(fi, exprs.length - 1)]!;
  const els = elementsOf(e, sets);
  const lit = new Set(els);
  const ab = inclusion({ k: 'set', i: 0 }, { k: 'set', i: 1 }, sets);
  const ba = inclusion({ k: 'set', i: 1 }, { k: 'set', i: 0 }, sets);
  const same = sameSet(e, f, sets);
  const fEls = elementsOf(f, sets);
  const odd = UNIVERSE.find((x) => lit.has(x) !== fEls.includes(x));
  const member = memberParts(e);

  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Nombre d’ensembles"
        value={String(n) as '2' | '3'}
        options={[
          { value: '2', label: '2 ensembles' },
          { value: '3', label: '3 ensembles' },
        ]}
        onChange={(v) => {
          setN(Number(v) as 2 | 3);
          setEi(3);
          setFi(10);
        }}
      />
      <div className="space-y-2">
        <p className="text-ink-700 text-sm">
          {fr('E = {1, 2, …, 12}. Choisis ce que contient chaque ensemble :')}
        </p>
        {sets.map((s, i) => (
          <label key={i} className="flex items-center gap-2" htmlFor={`${selectBase}-${i}`}>
            <span
              className="w-8 shrink-0 text-lg font-bold italic"
              style={{ color: SET_COLORS[i] }}
            >
              {SET_NAMES[i]}
            </span>
            <select
              id={`${selectBase}-${i}`}
              value={s.id}
              aria-label={`Ensemble ${SET_NAMES[i]}`}
              onChange={(ev) => setIds((old) => old.map((x, j) => (j === i ? ev.target.value : x)))}
              className="border-ink-200 text-ink-900 min-h-11 min-w-0 flex-1 rounded-xl border bg-transparent px-2 text-sm"
            >
              {SET_DEFS.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>

      <Venn n={n} sets={sets} expr={e} lit={lit} />

      <ExprChips
        exprs={exprs}
        value={exprs.indexOf(e)}
        onChange={setEi}
        label="Ensemble à colorer"
      />

      <section
        className="border-ink-100 space-y-1 rounded-2xl border px-3 py-2"
        data-testid="lg-set"
      >
        <MathText className="text-ink-900 block overflow-x-auto">{`$${setTex(e)} = ${listTex(els)}$`}</MathText>
        <MathText className="text-ink-700 block overflow-x-auto text-sm">
          {`$${member.lhs} \\iff$`}
        </MathText>
        <MathText className="text-ink-700 block overflow-x-auto text-sm">{`$\\quad {${member.rhs}}$`}</MathText>
        <p className="sr-only" aria-live="polite">
          {`${setSpeech(e)} : ${els.length ? els.join(', ') : 'ensemble vide'}`}
        </p>
      </section>

      <section
        className="border-ink-100 space-y-1 rounded-2xl border px-3 py-2"
        data-testid="lg-incl"
      >
        <p className="text-ink-600 text-sm font-semibold">Inclusion entre A et B</p>
        {[
          { a: 0, b: 1, r: ab },
          { a: 1, b: 0, r: ba },
        ].map(({ a, b, r }) => (
          <p key={a} className="text-ink-800 text-sm">
            {fr(
              r.holds
                ? `${SET_NAMES[a]} ⊂ ${SET_NAMES[b]} : tout élément de ${SET_NAMES[a]} est dans ${SET_NAMES[b]}. Pour tout x de E, « x ∈ ${SET_NAMES[a]} ⇒ x ∈ ${SET_NAMES[b]} » est vraie.`
                : `${SET_NAMES[a]} ⊄ ${SET_NAMES[b]} : ${r.witness} ∈ ${SET_NAMES[a]} mais ${r.witness} ∉ ${SET_NAMES[b]}, c’est un contre-exemple.`,
            )}
          </p>
        ))}
      </section>

      <ToggleChip on={cmp} onClick={() => setCmp((v) => !v)}>
        Comparer avec un autre ensemble
      </ToggleChip>
      {cmp && (
        <div className="space-y-2" data-testid="lg-compare">
          <ExprChips
            exprs={exprs}
            value={exprs.indexOf(f)}
            onChange={setFi}
            label="Ensemble à comparer"
          />
          <MathText className="text-ink-900 block overflow-x-auto">{`$${setTex(f)} = ${listTex(fEls)}$`}</MathText>
          <Note tone={same.identity && e !== f ? 'success' : 'neutral'}>
            {fr(
              e === f
                ? 'C’est la même expression : choisis-en une autre à comparer.'
                : same.identity
                  ? 'Toujours égaux, quels que soient les ensembles : chaque région du diagramme est coloriée de la même façon. C’est une loi (Morgan, distributivité…).'
                  : same.here
                    ? 'Égaux ICI, avec ces ensembles-là, mais pas en général : change un ensemble pour le voir.'
                    : `Différents : ${odd} est dans le ${lit.has(odd!) ? 'premier' : 'second'} ensemble mais pas dans le ${lit.has(odd!) ? 'second' : 'premier'}.`,
            )}
          </Note>
        </div>
      )}
    </div>
  );
}
